import { useCallback, useMemo, useRef, useState } from "react";
import { Button, Card, Popover } from "@introist/react-foundation/v2";
import { TraversableOption, TraversableOptionList } from "foundation/blocks/TraversableOptionList";
import { useIdentity } from "IdentityProvider";
import { apiClient } from "services/rpc/RpcProvider";
import {
  AttachedResource,
  canPick,
  RESOURCE_TYPES,
  resourceKey,
  SupportResource,
  SupportResourceType
} from "./resources";

type ResourcePickerProps = {
  onPick: (picked: AttachedResource) => void;
};

const ALL_AUTOMATION_STAGES: ("draft" | "active" | "ended")[] = ["draft", "active", "ended"];

type Root = "workflows" | "triggers" | "employees" | "forms";

// Steps and automations have no entry of their own: they are found under their workflow / employee
const ROOTS: { key: Root; title: string; type: SupportResourceType }[] = [
  { key: "workflows", title: "Workflows & steps", type: "workflow" },
  { key: "triggers", title: "Triggers", type: "trigger" },
  { key: "employees", title: "Employees & automations", type: "employee" },
  { key: "forms", title: "Forms", type: "form" }
];

const attach = (name: string | undefined, type: string) => `Attach ${name || `this ${type}`}`;

const path = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(" › ");

export const ResourcePicker = ({ onPick }: ResourcePickerProps) => {
  const { user } = useIdentity();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  // Leaf options are looked up by key on click, so loaders register what each key attaches
  const leaves = useRef(new Map<string, AttachedResource>());
  // Parent titles seen while traversing, used to label steps as "Parent › Step"
  const titles = useRef(new Map<string, string>());

  const roots = useMemo(() => ROOTS.filter(r => canPick(user, r.type)), [user]);
  const canSeeAutomations = canPick(user, "automation");

  const leaf = useCallback(
    (resource: SupportResource, title: string, label?: string): TraversableOption => {
      const key = resourceKey(resource);
      leaves.current.set(key, { resource, label: label || title });
      return { key, title, startAdornmentIcon: RESOURCE_TYPES[resource.type].icon };
    },
    []
  );

  const branch = useCallback((key: string, title: string): TraversableOption => {
    titles.current.set(key, title);
    return { key, title, path: true };
  }, []);

  const loadOptions = useCallback(
    async (optionPath: string): Promise<TraversableOption[]> => {
      const [, root, parentId, childId] = optionPath.split(".") as [
        string,
        Root | undefined,
        string | undefined,
        string | undefined
      ];

      if (!root) {
        return roots.map(r => ({
          ...branch(r.key, r.title),
          startAdornmentIcon: RESOURCE_TYPES[r.type].icon
        }));
      }

      switch (root) {
        case "workflows": {
          if (!parentId) {
            const workflows = await apiClient.workflows.v4.list.query({});
            return workflows.map(w => branch(w.id, w.title));
          }
          const workflowTitle = titles.current.get(parentId);
          const steps = await apiClient.workflows.v4.steps.list.query({ workflowId: parentId });
          return [
            leaf(
              { type: "workflow", id: parentId },
              attach(workflowTitle, "workflow"),
              workflowTitle
            ),
            ...steps.map(s => {
              const stepTitle = s.stepTitle ?? "Untitled step";
              return leaf(
                { type: "workflowStep", workflowId: parentId, id: s.id },
                stepTitle,
                path(workflowTitle, stepTitle)
              );
            })
          ];
        }
        case "triggers":
          return (await apiClient.triggers.list.query({})).map(t =>
            leaf({ type: "trigger", id: t.id }, t.name)
          );
        case "employees": {
          if (!parentId) {
            const employees = await apiClient.employees.v4.list.query({});
            return employees.flatMap(e =>
              e.id ? [branch(e.id, e.name ?? "Unnamed employee")] : []
            );
          }
          const employeeName = titles.current.get(parentId);
          const wholeEmployee = leaf(
            { type: "employee", id: parentId },
            attach(employeeName, "employee"),
            employeeName
          );
          if (!canSeeAutomations) return [wholeEmployee];

          if (!childId) {
            const automations = await apiClient.automations.list.query({
              employeeId: parentId,
              stages: ALL_AUTOMATION_STAGES
            });
            return [
              wholeEmployee,
              ...automations.map(a => ({
                ...branch(a.id, a.title),
                startAdornmentIcon: RESOURCE_TYPES.automation.icon
              }))
            ];
          }
          const automationTitle = titles.current.get(childId);
          const steps = await apiClient.automations.steps.list.query({ automationId: childId });
          return [
            leaf(
              { type: "automation", id: childId },
              attach(automationTitle, "automation"),
              path(employeeName, automationTitle)
            ),
            ...steps.map(s =>
              leaf(
                { type: "automationStep", automationId: childId, id: s.id },
                s.title,
                path(employeeName, automationTitle, s.title)
              )
            )
          ];
        }
        case "forms":
          return (await apiClient.forms.v2.list.query()).map(f =>
            leaf({ type: "form", id: f.id }, f.title)
          );
      }
      return [];
    },
    [roots, canSeeAutomations, leaf, branch]
  );

  if (roots.length === 0) return null;

  return (
    <>
      <div ref={setAnchor} style={{ alignSelf: "flex-start" }}>
        <Button variant="outlined" size="small" startIcon="plus" onClick={() => setOpen(!open)}>
          Add resource
        </Button>
      </div>
      <Popover
        referenceElement={anchor}
        open={open}
        onClose={() => setOpen(false)}
        closeOnContentClick={false}
      >
        <Card elevated style={{ padding: 0, width: "320px" }}>
          {/* Mounted only while open so every open starts from the type list */}
          {open && (
            <TraversableOptionList
              options={loadOptions}
              onItemClick={option => {
                const picked = leaves.current.get(option.key);
                if (!picked) return;
                onPick(picked);
                setOpen(false);
              }}
            />
          )}
        </Card>
      </Popover>
    </>
  );
};
